import { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";

import Header from "@/components/header/Header";
import CredentialsFooter from "@/components/footer/Footer";
import { PhoneNumberDuplicated } from "@/models/site-credentials/mobile/PhoneNumberDuplicated";
import { phoneNumberDuplicatedService } from "@/services/site-credentials/mobile/DuplicatedPhoneNumberAssociationService";
import { UserSignInResponse } from "@/models/site-credentials/mobile/UserSignInResponse";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import CredentialsForm from "@/components/Form/Form";

import megaphone from "@/assets/images/megaphone.svg";
import { FormError } from "@/models/FormError";
import { Button } from "@/components/ui/button";

type Form = PhoneNumberDuplicated & FormError;

export default function DuplicatedPhoneNumberAssociation() {
  const history = useHistory();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const location = useLocation<UserSignInResponse>();
  const phoneNumber =
    location.state != null ? location.state.properties.usernamePhoneNumber : "";

  const nextStep = location.state != null ? location.state.nextStep : "";

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const onSubmit: SubmitHandler<PhoneNumberDuplicated> = async (data) => {
    setLoading(true);
    try {
      const response = await phoneNumberDuplicatedService.phoneNumberDuplicated(
        {
          phoneNumber: phoneNumber,
        },
        nextStep
      );

      switch (response.kind) {
        case "success": {
          history.push(response.value.nextStep.substring(4), {
            ...response.value,
            responseCode: "100000",
          });
          break;
        }
        case "error":
          if (response.code === "300002") {
            setError("submitError", {
              message: t("common.unexpected_error"),
            });
            break;
          } else if (response.code === "300017") {
            history.push("/site-credentials", {
              responseCode: response.code,
            });
          }
          break;

        default:
          setError("submitError", { message: t("common.unexpected_error") });
          break;
      }

      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  return (
    <CredentialsForm
      id={"DuplicatedPhoneNumberAssociation"}
      autoComplete="off"
      form={formState}
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <Header
        title={t("site_credentials.alert")}
        subtitle={t("site_credentials.phonenumber_association.duplicated_information_description")}
        showLoading={loading}
        previousStepBackXTimes={2}
      />
      <div className="flex justify-center items-center">
        <img src={megaphone} alt="Callcenter image" />
      </div>
      <div className="flex justify-center">
        <Button
          type="submit"
          disabled={loading}
        >
          {t("site_credentials.button_next_message")}
        </Button>
      </div>
      <div
        role="note"
        aria-label={t("accessibility.signin.icons")}
        className="flex flex-col">
        <CredentialsFooter />
      </div>
    </CredentialsForm>
  );
}
